import React, { useCallback, useState } from "react";
import { uniqueId } from "lodash";
import styled from "styled-components";
import { toVWMobile, toREM } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { useMailingData } from "../../hooks/useMailingData";
import { KlaviyoService } from "../../services/klaviyo";
import { pushGtmDataLayer } from "../../helpers/analytics";
import SectionLines from "../SectionLines/SectionLines";

export type MailingListProps = {
  mainTitle: string;
  description: string;
  inputPlaceholder: string;
  buttonLabel: string;
  termsAndConditions: string;
  klaviyoListId: string;
  successMessage: string;
  checkbox: boolean;
};

const MailingListV2 = (props: { titleId?: string }): JSX.Element => {
  const data = useMailingData();
  const [email, setEmail] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(true);
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [emailInputId] = useState<string>(uniqueId("mailing-evolution-email-"));
  const [emailErrorId] = useState<string>(
    uniqueId("mailing-evolution-email-error-"),
  );

  const handleChange = useCallback(
    (e) => {
      e.preventDefault();
      setEmail(e.target.value);
      if (showEmailError && e.target.value) {
        setShowEmailError(false);
      }
    },
    [setEmail, showEmailError],
  );
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (email) {
        try {
          await KlaviyoService.subscribeMailingList(data?.klaviyoListId, {
            email,
          });
          setSubmitted(true);
          pushGtmDataLayer({
            event: "newsletter_sign_up",
          });
        } catch (error) {
          console.log(error);
        }
      } else {
        setShowEmailError(true);
      }
    },
    [email, data?.klaviyoListId],
  );

  const handleAgreementChange = () => {
    setAgreement(!agreement);
  };

  return (
    <MailingContainer>
      <Title id={props.titleId}>{data.mainTitle}</Title>
      <Content>
        {submitted ? (
          <Description>{data.successMessage}</Description>
        ) : (
          <>
            <Description>{data.description}</Description>
            <Form onSubmit={handleSubmit}>
              <InputLabel htmlFor={emailInputId}>
                {data.inputPlaceholder}
              </InputLabel>
              <EmailInput
                id={emailInputId}
                name="email"
                placeholder={data.inputPlaceholder}
                value={email}
                onChange={handleChange}
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                aria-invalid={showEmailError}
                aria-describedby={showEmailError ? emailErrorId : undefined}
                aria-label={data.inputPlaceholder}
              />
              <MailingButton
                type="submit"
                aria-disabled={!email}
                disabled={data.checkbox ? !agreement : false}
              >
                {data.buttonLabel}
              </MailingButton>
            </Form>
            {showEmailError && (
              <EmailError id={emailErrorId}>
                Please enter an email address.
              </EmailError>
            )}
            {data?.termsAndConditions && data?.checkbox ? (
              <CheckboxContainer>
                <CheckboxInput
                  name="agreement"
                  id="mailing-list-evolution-agreement"
                  type="checkbox"
                  checked={agreement}
                  onChange={handleAgreementChange}
                />
                <CheckboxLabel
                  htmlFor="mailing-list-evolution-agreement"
                  dangerouslySetInnerHTML={{ __html: data?.termsAndConditions }}
                />
              </CheckboxContainer>
            ) : (
              <Terms
                dangerouslySetInnerHTML={{
                  __html: data?.termsAndConditions,
                }}
              />
            )}
          </>
        )}
      </Content>
    </MailingContainer>
  );
};

const MailingContainer = styled.section`
  ${SectionLines("#6AB437")};
  width: 100%;
  background-color: #012604;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: ${toVWMobile(16)}vw;
  padding: ${toVWMobile(24)}vw ${toVWMobile(32)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex-direction: row;
    justify-content: flex-start;
    row-gap: 0;
    align-items: flex-start;
    margin: 0 auto;
    padding: ${toREM(40)}rem ${toREM(80)}rem;
  }

  &::before,
  &::after {
    width: 1px;
    background: #6ab437;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    &::before,
    &::after {
      background: rgba(246, 241, 231, 0.65);
    }
  }
`;

const Title = styled.p`
  width: 100%;
  font-size: ${toREM(20)}rem;
  line-height: normal;
  letter-spacing: -0.8px;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 600;
  color: #f6f1e7;
  text-align: left;
  text-transform: lowercase;

  &::first-letter {
    text-transform: uppercase;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(453.33)}rem;
    color: #fffbf4;
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${toVWMobile(16)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(458)}rem;
    min-height: ${toREM(154)}rem;
    gap: ${toREM(24)}rem;
  }
`;

const Description = styled.p`
  width: 100%;
  font-size: ${toREM(12)}rem;
  line-height: normal;
  letter-spacing: 0;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  color: #f6f1e7;
  text-align: left;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(14)}rem;
    line-height: normal;
    letter-spacing: 0;
    font-weight: 500;
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${toVWMobile(12)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${toREM(16)}rem;
  }
`;

const InputLabel = styled.label`
  position: absolute;
  width: ${toREM(1)}rem;
  height: ${toREM(1)}rem;
  padding: 0;
  margin: -${toREM(1)}rem;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const EmailInput = styled.input`
  width: auto;
  flex: 1 0 0;
  min-width: 0;
  height: ${toVWMobile(44)}vw;
  flex-shrink: 0;
  border: 1px solid #f6f1e7;
  border-radius: ${toREM(431.177)}rem;
  background: transparent;
  color: #f6f1e7;
  padding: ${toVWMobile(12)}vw ${toVWMobile(16)}vw ${toVWMobile(12)}vw
    ${toVWMobile(20)}vw;
  font-family: ${(props) => props.theme.fontGtAlpina},
    ${(props) => props.theme.fallBackFontFamily}, serif;
  font-size: ${toREM(12)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: -0.6px;

  &::placeholder {
    color: #f6f1e7;
    opacity: 0.5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #fffbf4;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    box-sizing: content-box;
    width: ${toREM(284)}rem;
    height: ${toREM(20)}rem;
    padding: ${toREM(12)}rem ${toREM(16)}rem ${toREM(12)}rem ${toREM(24)}rem;
    border-width: 1px;
    font-size: ${toREM(16)}rem;
    line-height: ${toREM(20)}rem;
    letter-spacing: -0.8px;

    &::placeholder {
      color: #f6f1e7;
      opacity: 0.5;
      font-family: ${(props) => props.theme.fontGtAlpina},
        ${(props) => props.theme.fallBackFontFamily}, serif;
      font-size: ${toREM(16)}rem;
      font-style: normal;
      font-weight: 300;
      line-height: normal;
      letter-spacing: -0.8px;
    }
  }
`;

const EmailError = styled.p`
  margin: 0;
  margin-top: ${toVWMobile(6)}vw;
  color: #fffbf4;
  font-size: ${toREM(12)}rem;
  line-height: ${toREM(16)}rem;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-top: ${toREM(8)}rem;
    font-size: ${toREM(12)}rem;
    line-height: ${toREM(16)}rem;
  }
`;

const MailingButton = styled.button`
  width: ${toVWMobile(118)}vw;
  height: ${toVWMobile(44)}vw;
  flex-shrink: 0;
  border: none;
  background: #fffbf4;
  border-radius: ${toREM(431.177)}rem;
  color: #012604;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: ${toVWMobile(12)}vw ${toVWMobile(16)}vw;
  cursor: pointer;
  font-family: ${(props) => props.theme.fontGtAlpina},
    ${(props) => props.theme.fallBackFontFamily}, serif;
  font-size: ${toREM(12)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  letter-spacing: -0.6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #fffbf4;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: auto;
    min-width: 0;
    height: ${toREM(44)}rem;
    font-size: ${toREM(16)}rem;
    font-weight: 300;
    letter-spacing: -0.8px;
    line-height: ${toREM(11)}rem;
    padding: ${toREM(16)}rem ${toREM(32)}rem;
  }

  &:hover,
  &:focus-visible,
  &:active {
    background: var(--reskin-color-butterleaf);
    color: var(--reskin-color-dark-green);
  }
`;

const Terms = styled.p`
  width: 100%;
  font-size: ${toREM(10)}rem;
  line-height: normal;
  letter-spacing: 0;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  opacity: 0.7;
  color: #f6f1e7;
  text-align: left;

  a {
    color: #f6f1e7;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(350)}rem;
  }
`;

const CheckboxContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: ${toREM(12)}rem;
  letter-spacing: 0;
  color: #f6f1e7;
  text-align: left;
  gap: ${toVWMobile(12)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${toREM(12)}rem;
  }
`;

const CheckboxInput = styled.input`
  appearance: none;
  width: ${toVWMobile(16)}vw;
  height: ${toVWMobile(16)}vw;
  border: 1px solid #f6f1e7;
  background: transparent;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;

  &:checked {
    background: #fffbf4;
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 44%;
    width: ${toVWMobile(7)}vw;
    height: ${toVWMobile(4)}vw;
    border-left: 1px solid #012604;
    border-bottom: 1px solid #012604;
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &:focus-visible {
    outline: 2px solid #fffbf4;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(16)}rem;
    height: ${toREM(16)}rem;

    &:checked::after {
      width: ${toREM(7)}rem;
      height: ${toREM(4)}rem;
    }
  }
`;

const CheckboxLabel = styled.label`
  color: #f6f1e7;
  text-align: left;
  font-size: ${toREM(8)}rem;
  font-style: normal;
  line-height: normal;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 500;
  opacity: 0.7;
  width: 100%;

  a {
    color: #f6f1e7;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toREM(350)}rem;
    font-size: ${toREM(10)}rem;
  }
`;

export default MailingListV2;
